import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withPrefix } from 'gatsby';
import Markdown from 'markdown-to-jsx';
import differenceInMinutes from 'date-fns/differenceInMinutes';

import { useMouseOut } from '../utils/useMouseOut';

import Answers from './answers';
import QuestionExplanation from './questionExplanation';
import Button from './button';
import Popup from './popup';

import { popupDiplomaSeen } from '../state/app/actions';
import { logoutAction } from '../state/auth/actions';
import { quizRestart, diplomaDownloaded } from '../state/quiz/actions';

import quizData from '../../content/quiz.json';

import trophyUrl from '../images/Puchar_v2.png';
import logoUrl from '../images/logosko.svg';

const renderHTML = rawHTML => React.createElement('span', { dangerouslySetInnerHTML: { __html: rawHTML } });

const ResultSuccess = ({ popupSeen, title, description, desclimer, button, reset, setPopupSeen, diplomaDownloaded, setDiplomaDownloaded, onReset }) => {
  const [popup, setPopup] = useState(false);

  const showPopup = () => {
    if (!popupSeen) {
      setPopup(true);
    }
  };

  const hidePopup = () => {
    setPopup(false);
    setPopupSeen();
    setDiplomaDownloaded(true);
  };

  const justClosePopup = () => {
    setPopup(false);
    setPopupSeen();
  };

  useMouseOut({ onlyTop: true }, showPopup);

  useEffect(() => {
    if (!diplomaDownloaded) {
      const myConfirmation = e => {
        const message = 'Pobierz i wydrukuj swój dyplom.';
        e.returnValue = message;
        return message;
      };

      window.onbeforeunload = myConfirmation;

      return () => {
        window.onbeforeunload = undefined;
      };
    }
  }, [diplomaDownloaded]);

  return (
    <div className="result">
      <img className="result__image" src={trophyUrl} />
      <div className="result__title">{title}</div>
      <p className="result__description">{renderHTML(description)}</p>
      <p className="result__desclimer">{renderHTML(desclimer)}</p>
      <div className="result__button">
        <a href={withPrefix('/SKO_Test_Wiedzy_Ekonomicznej_dyplom.pdf')} className="button button--mobile-100" download onClick={hidePopup}>
          {button}
        </a>
      </div>
      {diplomaDownloaded && (
        <div className="result__button">
          <button className="button button--mobile-100" onClick={onReset} type="button">
            {reset}
          </button>
        </div>
      )}

      {!diplomaDownloaded && popup && (
        <Popup onClose={justClosePopup}>
          <div className="popup__text">Pobierz i wydrukuj swój dyplom 😊</div>
          <div className="popup__buttons">
            <a href={withPrefix('/SKO_Test_Wiedzy_Ekonomicznej_dyplom.pdf')} className="button button--mobile-100" download onClick={hidePopup}>
              Chcę pobrać dyplom
            </a>
          </div>
        </Popup>
      )}
    </div>
  );
};

const ResultFailure = ({ title, description, button, onRestart }) => (
  <div className="result">
    <img className="result__image" src={logoUrl} />
    <div className="result__title">{title}</div>
    <p className="result__description">{renderHTML(description)}</p>
    <div className="result__button">
      <Button onClick={onRestart} mobile>
        {button}
      </Button>
    </div>
  </div>
);

const Result = ({ popupSeen, percentage, timeout, diplomaDownloaded, setPopupSeen, restartQuiz, logoutStudent, setDiplomaDownloaded }) => {
  if (percentage >= quizData.successTreshold) {
    if (!timeout) {
      return (
        <ResultSuccess
          {...quizData.results.success}
          popupSeen={popupSeen}
          setPopupSeen={setPopupSeen}
          diplomaDownloaded={diplomaDownloaded}
          setDiplomaDownloaded={setDiplomaDownloaded}
          onReset={logoutStudent}
        />
      );
    } else {
      return (
        <ResultSuccess
          {...quizData.results.successTimeout}
          popupSeen={popupSeen}
          setPopupSeen={setPopupSeen}
          diplomaDownloaded={diplomaDownloaded}
          setDiplomaDownloaded={setDiplomaDownloaded}
          onReset={logoutStudent}
        />
      );
    }
  } else {
    if (!timeout) {
      return <ResultFailure {...quizData.results.failure} onRestart={restartQuiz} />;
    } else {
      return <ResultFailure {...quizData.results.failureTimeout} onRestart={restartQuiz} />;
    }
  }
};

const Summary = ({ questions, validAnswers }) => {
  return (
    <div>
      <div className="result-points">
        <div className="result-points__title">Twoje odpowiedzi:</div>
        <div className="result-points__points">
          Twój wynik to {validAnswers}
          <span>/{quizData.numberOfQuestions}</span>
        </div>
      </div>
      {questions
        .filter(question => question.checked.length > 0)
        .map((question, index) => (
          <div key={question.id} className="question">
            <div className="question__content question__content--no-margin">
              <div className="question__number">
                Pytanie {index + 1} z {quizData.numberOfQuestions}
              </div>
              <div className="question__text">{question.question}</div>
              <div className="question__answers">
                <Answers question={question} validate />
              </div>
              {/* {question.explanation && (
                <div className="question__explanation">
                  <QuestionExplanation {...question.explanation} />
                </div>
              )} */}
            </div>
          </div>
        ))}
    </div>
  );
};

const Results = ({
  popupSeen,
  percentage,
  timeout,
  questions,
  validAnswers,
  diplomaDownloaded,
  setPopupSeen,
  restartQuiz,
  setDiplomaDownloaded,
  logoutStudent,
}) => {
  return (
    <>
      <Result
        popupSeen={popupSeen}
        percentage={percentage}
        timeout={timeout}
        diplomaDownloaded={diplomaDownloaded}
        setPopupSeen={setPopupSeen}
        restartQuiz={restartQuiz}
        logoutStudent={logoutStudent}
        setDiplomaDownloaded={setDiplomaDownloaded}
      />
      {percentage >= quizData.successTreshold && <Summary questions={questions} validAnswers={validAnswers} />}
    </>
  );
};

const mapStateToProps = state => {
  return {
    popupSeen: state.root.app.popupDiplomaSeen,
    timeout: differenceInMinutes(state.root.quiz.endTime, state.root.quiz.finishTime) === 0,
    percentage: state.root.quiz.percentage,
    questions: state.root.quiz.questionsForStudent,
    validAnswers: state.root.quiz.validAnswers,
    diplomaDownloaded: state.root.quiz.diplomaDownloaded,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    setPopupSeen: () => dispatch(popupDiplomaSeen()),
    restartQuiz: () => dispatch(quizRestart()),
    setDiplomaDownloaded: () => dispatch(diplomaDownloaded()),
    logoutStudent: () => dispatch(logoutAction()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Results);
